import { useQuery } from "@apollo/client";
import { FormControl } from "@mui/material";
import { DocumentNode } from "graphql";
import { TAGS_QUERY } from "queries/tags";
import { useState, useMemo } from "react";
import { useDebounce } from "use-debounce";

import { MultiValueTextInput } from "./MultiValueTextInput";

interface UnifiedTagsInputProps {
  value?: string[] | null;
  onChange: (tags: string[]) => void;
  label?: string;
  onRequestApply?: () => void;
  // Optional GraphQL query to fetch available tags
  tagsQuery?: DocumentNode;
  // Optional query configuration when using a tagsQuery
  queryConfig?: {
    tenantId?: string;
    additionalSearch?: string;
  };
  placeholder?: string;
  size?: "small" | "medium";
}

export function UnifiedTagsInput({
  value,
  onChange,
  label = "Tags",
  onRequestApply,
  tagsQuery,
  queryConfig,
  placeholder = "Press enter or comma to add",
  size = "small",
}: UnifiedTagsInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue] = useDebounce(inputValue, 300);

  // Build search query for backend
  const searchQuery = useMemo(() => {
    if (!tagsQuery) return null;

    // Workflow tags (type:Workflow) don't require tenantId - they're staff-only internal tags
    const isWorkflowTags = queryConfig?.additionalSearch?.includes("type:Workflow");

    // All other tag types require tenantId for security
    if (!queryConfig?.tenantId && !isWorkflowTags) {
      return null;
    }

    let query = "";
    if (queryConfig?.tenantId) {
      query = `tenantId:${queryConfig.tenantId}`;
    }

    if (queryConfig?.additionalSearch) {
      query += query ? ` ${queryConfig.additionalSearch}` : queryConfig.additionalSearch;
    }

    // Append name filter if user typed something (server-side search)
    if (debouncedInputValue.trim()) {
      query += ` name:${debouncedInputValue.trim()}`;
    }

    return query;
  }, [tagsQuery, queryConfig, debouncedInputValue]);

  // Query backend tags if configured
  const { data, loading } = useQuery(tagsQuery || TAGS_QUERY, {
    fetchPolicy: "cache-first",
    variables: {
      search: searchQuery,
      sort: [{ field: "name", sort: "asc" }],
      page: 0,
      pageSize: 1000,
    },
    skip: !tagsQuery || !searchQuery,
  });

  // Get available tags from backend or empty array
  const availableTags = useMemo(() => {
    if (!data?.tags?.items) return [];
    return data.tags.items.map((tag: any) => tag.name).sort();
  }, [data]);

  // Combine backend tags with current value for options
  // This ensures user-entered tags that aren't in backend still show up
  const allOptions = useMemo(() => {
    const currentTags = value ?? [];
    const combined = new Set([...availableTags, ...currentTags]);
    return Array.from(combined).sort();
  }, [availableTags, value]);

  return (
    <FormControl fullWidth>
      <MultiValueTextInput
        value={value}
        onChange={(values) => onChange([...values].sort())}
        options={allOptions}
        loading={loading && !!tagsQuery}
        onInputChange={setInputValue}
        onRequestApply={onRequestApply}
        label={label}
        placeholder={placeholder}
        size={size}
      />
    </FormControl>
  );
}
